import React, { useState, useRef, useCallback, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  ColumnDef,
  CellContext,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
} from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import {
  CheckCircleIcon,
  WarningIcon,
  InfoIcon,
  TrashIcon,
  ArrowUUpLeftIcon,
  ProhibitIcon,
  FileMagnifyingGlassIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  LineItem,
  type InventoryValidationResult,
  type DiscountValidationResult,
} from 'src/types/application/SalesOrderDraftDTO';
import { SalesOrderDraftAssociateLineItemPopover } from '../SalesOrderDraftAssociateLineItemPopover';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import DiscountBadge from './DiscountBadge';
import InventoryBadge from './InventoryBadge';
import { DualPriceCell } from './DualPriceCell';
import {
  getErpUnitPrice,
  getListPrice,
  calculateDiscountPercent,
  calculateTotal,
  formatCurrency,
} from './priceUtils';
import { useUpdateDiscountVerification } from '@/api/accounts-receivable/sales-order-drafts/useUpdateDiscountVerification';

// Types for cell navigation
type EditableField =
  | 'quantity'
  | 'unitPrice'
  | 'rescheduledDate'
  | 'customerDescription'
  | 'customerIdentifier';

interface Props {
  lineItems: LineItem[];
  editable: boolean;
  isLoading: boolean;
  onDeleteLineItem: (index: number) => void;
  onUpdateLineItem?: (
    index: number,
    field:
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier',
    value: number | string,
  ) => void;
  pendingUpdates?: Record<
    number,
    Set<
      | 'quantity'
      | 'unitPrice'
      | 'rescheduledDate'
      | 'customerDescription'
      | 'customerIdentifier'
    >
  >;
  onSelectLineItem?: (index: number) => void;
  isEdiSource?: boolean;
  isEmailSource?: boolean;
  onToggleRejectLineItem?: (index: number) => void;
  customerId?: string;
  customerName?: string;
  onAssociateVariantSelected?: (
    rowIndex: number,
    item: LineItem,
    productVariantId?: string,
  ) => void;
  /** Inventory validation results by productVariantId - required for enhanced table */
  inventoryResults: Record<string, InventoryValidationResult>;
  /** Whether inventory validation is loading */
  isInventoryLoading: boolean;
  /** Discount validation results by productVariantId - required for enhanced table */
  discountResults: Record<string, DiscountValidationResult>;
  /** Whether discount validation is loading */
  isDiscountLoading: boolean;
  /** Staged purchase order ID - needed for verification mutations */
  stagedPurchaseOrderId?: string;
  /** Whether to show the inventory column - enabled via chat widget */
  showInventoryColumn?: boolean;
}

type CellPosition = {
  rowIndex: number;
  field: EditableField;
};

// Navigation manager hook
function useTableNavigation(
  lineItems: LineItem[],
  isEmailSource: boolean,
  _onUpdateLineItem?: (
    index: number,
    field: EditableField,
    value: number | string,
  ) => void,
) {
  const [currentEditingCell, setCurrentEditingCell] =
    useState<CellPosition | null>(null);
  const [isNavigationActive, setIsNavigationActive] = useState(false);
  const cellRefs = useRef<
    Map<string, { startEdit: () => void; saveAndExit: () => void }>
  >(new Map());
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Get all editable fields based on email source
  const getEditableFields = useCallback((): EditableField[] => {
    const fields: EditableField[] = [
      'customerIdentifier',
      'customerDescription',
      'quantity',
      'unitPrice',
    ];
    if (!isEmailSource) {
      fields.push('rescheduledDate');
    }
    return fields;
  }, [isEmailSource]);

  // Get cell key for referencing
  const getCellKey = useCallback((rowIndex: number, field: EditableField) => {
    return `${rowIndex}-${field}`;
  }, []);

  // Register cell ref
  const registerCellRef = useCallback(
    (
      rowIndex: number,
      field: EditableField,
      ref: { startEdit: () => void; saveAndExit: () => void },
    ) => {
      const key = getCellKey(rowIndex, field);
      cellRefs.current.set(key, ref);
    },
    [getCellKey],
  );

  // Navigate to next/previous cell
  const navigateToCell = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right' | 'tab' | 'shift-tab') => {
      if (!currentEditingCell) return false;

      const { rowIndex, field } = currentEditingCell;
      const editableFields = getEditableFields();
      const currentFieldIndex = editableFields.indexOf(field);

      let newRowIndex = rowIndex;
      let newFieldIndex = currentFieldIndex;

      switch (direction) {
        case 'left':
        case 'shift-tab':
          newFieldIndex = currentFieldIndex - 1;
          if (newFieldIndex < 0) {
            newFieldIndex = editableFields.length - 1;
            newRowIndex = rowIndex - 1;
          }
          break;
        case 'right':
        case 'tab':
          newFieldIndex = currentFieldIndex + 1;
          if (newFieldIndex >= editableFields.length) {
            newFieldIndex = 0;
            newRowIndex = rowIndex + 1;
          }
          break;
        case 'up':
          newRowIndex = rowIndex - 1;
          break;
        case 'down':
          newRowIndex = rowIndex + 1;
          break;
      }

      // Check bounds
      if (newRowIndex < 0 || newRowIndex >= lineItems.length) {
        return false;
      }

      // Save current cell before navigating away
      const currentCellKey = getCellKey(rowIndex, field);
      const currentCellRef = cellRefs.current.get(currentCellKey);
      if (currentCellRef) {
        currentCellRef.saveAndExit();
      }

      // Navigate to the new cell
      const newField = editableFields[newFieldIndex];
      const newCellKey = getCellKey(newRowIndex, newField);
      const newCellRef = cellRefs.current.get(newCellKey);

      if (newCellRef) {
        setCurrentEditingCell({ rowIndex: newRowIndex, field: newField });
        // Small delay to ensure previous cell has exited edit mode
        setTimeout(() => {
          newCellRef.startEdit();
        }, 10);
        return true;
      }

      return false;
    },
    [currentEditingCell, getEditableFields, getCellKey, lineItems.length],
  );

  // Start editing a cell
  const startEditingCell = useCallback(
    (rowIndex: number, field: EditableField) => {
      setCurrentEditingCell({ rowIndex, field });
      setIsNavigationActive(true);
    },
    [],
  );

  // Stop editing but keep navigation active
  const stopEditingCell = useCallback(() => {
    setCurrentEditingCell(null);
    // Don't deactivate navigation - only do that on explicit exit
  }, []);

  // Exit navigation mode completely
  const exitNavigationMode = useCallback(() => {
    // Save current cell if it exists
    if (currentEditingCell) {
      const currentCellKey = getCellKey(
        currentEditingCell.rowIndex,
        currentEditingCell.field,
      );
      const currentCellRef = cellRefs.current.get(currentCellKey);
      if (currentCellRef) {
        currentCellRef.saveAndExit();
      }
    }
    setCurrentEditingCell(null);
    setIsNavigationActive(false);
  }, [currentEditingCell, getCellKey]);

  // Global keyboard event handler for navigation
  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (!isNavigationActive) return;

      // Handle Escape to exit navigation mode
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        exitNavigationMode();
        return;
      }

      // Handle navigation keys
      if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
          e.key,
        )
      ) {
        e.preventDefault();
        e.stopPropagation();

        if (currentEditingCell) {
          const direction =
            e.key === 'ArrowLeft'
              ? 'left'
              : e.key === 'ArrowRight'
                ? 'right'
                : e.key === 'ArrowUp'
                  ? 'up'
                  : e.key === 'ArrowDown'
                    ? 'down'
                    : e.shiftKey
                      ? 'shift-tab'
                      : 'tab';
          navigateToCell(direction);
        }
      }
    };

    if (isNavigationActive) {
      document.addEventListener('keydown', handleGlobalKeyDown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [
    isNavigationActive,
    currentEditingCell,
    navigateToCell,
    exitNavigationMode,
  ]);

  // Handle clicks outside the table
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isNavigationActive &&
        tableContainerRef.current &&
        !tableContainerRef.current.contains(event.target as Node)
      ) {
        // Only exit navigation if clicking outside the table
        exitNavigationMode();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNavigationActive, exitNavigationMode]);

  return {
    currentEditingCell,
    isNavigationActive,
    navigateToCell,
    startEditingCell,
    stopEditingCell,
    exitNavigationMode,
    registerCellRef,
    tableContainerRef,
  };
}

// Inline editable cell component
function EditableCell({
  value,
  onSave,
  inputType = 'number',
  type = 'number',
  prefix = '',
  isPending = false,
  rowIndex,
  field,
  navigationManager,
  placeholder = '',
}: {
  value: number | string | undefined;
  onSave: (value: number | string) => void;
  inputType?: 'number' | 'text';
  type?: 'number' | 'integer';
  prefix?: string;
  isPending?: boolean;
  rowIndex: number;
  field: EditableField;
  navigationManager: ReturnType<typeof useTableNavigation>;
  placeholder?: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    currentEditingCell,
    startEditingCell,
    stopEditingCell,
    registerCellRef,
  } = navigationManager;

  // Check if this cell is currently being edited
  const isCurrentlyEditing =
    currentEditingCell?.rowIndex === rowIndex &&
    currentEditingCell?.field === field;

  // Sync editing state with navigation manager
  React.useEffect(() => {
    if (isCurrentlyEditing && !isEditing) {
      setIsEditing(true);
      setEditValue(value?.toString() || '');
    } else if (!isCurrentlyEditing && isEditing) {
      setIsEditing(false);
    }
  }, [isCurrentlyEditing, isEditing, value]);

  // Auto-focus when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure the input is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Only call select if it's available (not all input types support it)
          if (typeof inputRef.current.select === 'function') {
            inputRef.current.select();
          }
        }
      }, 0);
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    if (inputType === 'text') {
      const trimmedValue = editValue.trim();
      // Only call onSave if the value actually changed
      if (trimmedValue !== value) {
        onSave(trimmedValue);
      }
    } else {
      const numValue = parseFloat(editValue);
      if (!isNaN(numValue) && numValue >= 0) {
        // Only call onSave if the value actually changed
        if (numValue !== value) {
          onSave(numValue);
        }
      }
    }
    setIsEditing(false);
    stopEditingCell();
  }, [editValue, value, onSave, stopEditingCell, inputType]);

  // Register this cell with the navigation manager
  React.useEffect(() => {
    registerCellRef(rowIndex, field, {
      startEdit: () => {
        setIsEditing(true);
        setEditValue(value?.toString() || '');
      },
      saveAndExit: () => {
        handleSave();
      },
    });
  }, [registerCellRef, rowIndex, field, value, handleSave]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Only handle Enter and Escape keys locally
    // Navigation keys are handled globally by the navigation manager
    if (e.key === 'Enter') {
      e.stopPropagation();
      handleSave();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      // On escape, revert to original value and exit editing
      setEditValue(value?.toString() || '');
      setIsEditing(false);
      stopEditingCell();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when the field gets focus
    if (typeof e.target.select === 'function') {
      e.target.select();
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      startEditingCell(rowIndex, field);
      setIsEditing(true);
    }
  };

  if (isEditing) {
    return (
      <MDBox
        display="flex"
        alignItems="center"
        gap={0.5}
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      >
        <TextField
          ref={inputRef}
          size="small"
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEditValue(e.target.value)
          }
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          onFocus={handleFocus}
          autoFocus
          type={inputType === 'text' ? 'text' : 'number'}
          placeholder={placeholder}
          inputProps={
            inputType === 'text'
              ? {}
              : { min: 0, step: type === 'integer' ? 1 : 0.01 }
          }
          sx={{
            width: '100%',
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              padding: '4px 8px',
            },
            // Hide number input spinner arrows
            '& input[type=number]': {
              '-moz-appearance': 'textfield',
            },
            '& input[type=number]::-webkit-outer-spin-button': {
              '-webkit-appearance': 'none',
              margin: 0,
            },
            '& input[type=number]::-webkit-inner-spin-button': {
              '-webkit-appearance': 'none',
              margin: 0,
            },
          }}
        />
      </MDBox>
    );
  }

  // Format display value based on type
  const formatValue = (val: number | string | undefined) => {
    if (inputType === 'text') {
      return val?.toString() || placeholder;
    }

    if (type === 'integer') {
      // For quantity: show whole numbers unless there's a decimal
      const numVal = Number(val);
      return numVal % 1 === 0 ? numVal.toString() : numVal.toFixed(2);
    } else {
      // For currency: always show 2 decimal places
      return Number(val).toFixed(2);
    }
  };

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        minHeight: '24px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          border: '1px solid rgba(25, 118, 210, 0.3)',
        },
        border: isPending
          ? '1px solid rgba(25, 118, 210, 0.5)'
          : '1px solid transparent',
        backgroundColor: isPending ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
        ...(isPending && {
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
            '50%': {
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              borderColor: 'rgba(25, 118, 210, 0.7)',
            },
            '100%': {
              backgroundColor: 'rgba(25, 118, 210, 0.05)',
              borderColor: 'rgba(25, 118, 210, 0.5)',
            },
          },
        }),
      }}
      onClick={handleClick}
    >
      <MDTypography
        variant="caption"
        color={inputType === 'text' && !value ? 'text.secondary' : undefined}
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          display: 'block',
        }}
        title={
          inputType === 'text'
            ? formatValue(value)
            : value
              ? `${prefix}${formatValue(value)}`
              : '-'
        }
      >
        {inputType === 'text'
          ? formatValue(value)
          : value
            ? `${prefix}${formatValue(value)}`
            : '-'}
      </MDTypography>
    </MDBox>
  );
}

// Inline editable date cell component
function EditableDateCell({
  value,
  onSave,
  isPending = false,
  rowIndex,
  field,
  navigationManager,
}: {
  value: string | undefined;
  onSave: (value: string) => void;
  isPending?: boolean;
  rowIndex: number;
  field: EditableField;
  navigationManager: ReturnType<typeof useTableNavigation>;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    currentEditingCell,
    startEditingCell,
    stopEditingCell,
    registerCellRef,
  } = navigationManager;

  // Check if this cell is currently being edited
  const isCurrentlyEditing =
    currentEditingCell?.rowIndex === rowIndex &&
    currentEditingCell?.field === field;

  // Sync editing state with navigation manager
  React.useEffect(() => {
    if (isCurrentlyEditing && !isEditing) {
      setIsEditing(true);
      setEditValue(value || '');
    } else if (!isCurrentlyEditing && isEditing) {
      setIsEditing(false);
    }
  }, [isCurrentlyEditing, isEditing, value]);

  // Auto-focus when entering edit mode
  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      // Small delay to ensure the input is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Only call select if it's available (not all input types support it)
          if (typeof inputRef.current.select === 'function') {
            inputRef.current.select();
          }
        }
      }, 0);
    }
  }, [isEditing]);

  const handleSave = useCallback(() => {
    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (editValue && !dateRegex.test(editValue)) {
      // Invalid format, revert
      setEditValue(value || '');
      setIsEditing(false);
      stopEditingCell();
      return;
    }

    // Only call onSave if the value actually changed
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
    stopEditingCell();
  }, [editValue, value, onSave, stopEditingCell]);

  // Register this cell with the navigation manager
  React.useEffect(() => {
    registerCellRef(rowIndex, field, {
      startEdit: () => {
        setIsEditing(true);
        setEditValue(value || '');
      },
      saveAndExit: () => {
        handleSave();
      },
    });
  }, [registerCellRef, rowIndex, field, value, handleSave]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Only handle Enter and Escape keys locally
    // Navigation keys are handled globally by the navigation manager
    if (e.key === 'Enter') {
      e.stopPropagation();
      handleSave();
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      // On escape, revert to original value and exit editing
      setEditValue(value || '');
      setIsEditing(false);
      stopEditingCell();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all text when the field gets focus
    if (typeof e.target.select === 'function') {
      e.target.select();
    }
  };

  const handleClick = () => {
    if (!isEditing) {
      startEditingCell(rowIndex, field);
      setIsEditing(true);
    }
  };

  if (isEditing) {
    return (
      <MDBox
        display="flex"
        alignItems="center"
        gap={0.5}
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
      >
        <TextField
          ref={inputRef}
          size="small"
          value={editValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEditValue(e.target.value)
          }
          onKeyDown={handleKeyPress}
          onBlur={handleSave}
          onFocus={handleFocus}
          autoFocus
          type="date"
          placeholder="YYYY-MM-DD"
          sx={{
            width: '100%',
            '& .MuiInputBase-input': {
              fontSize: '0.75rem',
              padding: '4px 8px',
            },
          }}
        />
      </MDBox>
    );
  }

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      }}
      onClick={handleClick}
    >
      <MDTypography variant="caption" sx={{ opacity: isPending ? 0.5 : 1 }}>
        {value ? new Date(value + 'T12:00:00Z').toLocaleDateString() : '-'}
      </MDTypography>
    </MDBox>
  );
}

/**
 * Enhanced version of LineItemsTable that always includes discount and inventory
 * validation columns. This component is designed for customers that require
 * full validation features without relying on feature flags.
 */
export function EnhancedLineItemsTable({
  lineItems,
  editable,
  isLoading,
  onDeleteLineItem,
  onUpdateLineItem,
  pendingUpdates = {},
  onSelectLineItem,
  isEdiSource,
  isEmailSource,
  onToggleRejectLineItem,
  customerId,
  customerName,
  onAssociateVariantSelected,
  inventoryResults,
  isInventoryLoading,
  discountResults,
  isDiscountLoading,
  stagedPurchaseOrderId,
  showInventoryColumn,
}: Props) {
  const { enabled: isAssociateProductV2Enabled } = useFeatureFlag(
    'associate-product-v2-enabled',
    false,
  );

  // Helper to look up discount validation by productVariantId or SKU
  const getDiscountValidation = useCallback(
    (item: LineItem) => {
      // Try selectedProductVariantId first
      if (
        item.selectedProductVariantId &&
        discountResults[item.selectedProductVariantId]
      ) {
        return discountResults[item.selectedProductVariantId];
      }
      // Fall back to SKU lookup (customerIdentifier, sku, or upc)
      const sku = item.customerIdentifier || item.sku || item.upc;
      if (sku && discountResults[sku]) {
        return discountResults[sku];
      }
      return undefined;
    },
    [discountResults],
  );

  // Helper to get productVariantId for a line item, checking both direct assignment and SKU-based lookup
  // The backend returns results keyed by BOTH productVariantId AND SKU, so we can find the productVariantId
  // by checking if any UUID key in discountResults maps to an equivalent result as the SKU
  const getProductVariantIdForItem = useCallback(
    (item: LineItem): string | undefined => {
      // If item has a direct selectedProductVariantId, use that
      if (item.selectedProductVariantId) {
        return item.selectedProductVariantId;
      }

      // If we found a result via SKU lookup, find the corresponding productVariantId key
      const sku = item.customerIdentifier || item.sku || item.upc;
      if (sku && discountResults[sku]) {
        const skuResult = discountResults[sku];
        // Look for a UUID key that maps to an equivalent result
        // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        // Note: We compare by value (poUnitPrice + status) since JSON serialization
        // creates independent objects, breaking reference equality
        const uuidRegex =
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        for (const key of Object.keys(discountResults)) {
          if (uuidRegex.test(key)) {
            const uuidResult = discountResults[key];
            // Compare by poUnitPrice and status - these should uniquely identify the same validation result
            if (
              uuidResult.poUnitPrice === skuResult.poUnitPrice &&
              uuidResult.status === skuResult.status
            ) {
              return key;
            }
          }
        }
      }

      return undefined;
    },
    [discountResults],
  );

  // Helper to look up inventory validation by productVariantId or SKU
  const getInventoryValidation = useCallback(
    (item: LineItem) => {
      // Try selectedProductVariantId first
      if (
        item.selectedProductVariantId &&
        inventoryResults[item.selectedProductVariantId]
      ) {
        return inventoryResults[item.selectedProductVariantId];
      }
      // Fall back to SKU lookup (customerIdentifier, sku, or upc)
      const sku = item.customerIdentifier || item.sku || item.upc;
      if (sku && inventoryResults[sku]) {
        return inventoryResults[sku];
      }
      return undefined;
    },
    [inventoryResults],
  );

  // Check if a line item has a lookupable key (for loading state)
  const hasLookupKey = useCallback((item: LineItem) => {
    return !!(
      item.selectedProductVariantId ||
      item.customerIdentifier ||
      item.sku ||
      item.upc
    );
  }, []);

  // Initialize navigation manager
  const navigationManager = useTableNavigation(
    lineItems,
    !!isEmailSource,
    onUpdateLineItem,
  );

  // Mutation for updating discount verification status
  const updateVerificationMutation = useUpdateDiscountVerification();

  // Track which line item is currently being waived
  const [waivingLineItemIndex, setWaivingLineItemIndex] = useState<
    number | null
  >(null);

  // Handler for waiving a customer-specific discount verification
  const handleWaiveDiscount = useCallback(
    (lineItemIndex: number) => {
      if (!stagedPurchaseOrderId) return;

      setWaivingLineItemIndex(lineItemIndex);
      updateVerificationMutation.mutate(
        {
          stagedPurchaseOrderId,
          lineItemIndex,
          status: 'WAIVED',
        },
        {
          onSettled: () => {
            setWaivingLineItemIndex(null);
          },
        },
      );
    },
    [stagedPurchaseOrderId, updateVerificationMutation],
  );

  // Column definitions - always include discount and inventory columns
  const columns: ColumnDef<LineItem>[] = useMemo(
    () => [
      {
        id: 'lineNumber',
        header: () => (
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 600,
              textAlign: 'right',
              display: 'block',
              paddingRight: 4,
            }}
          >
            #
          </MDTypography>
        ),
        cell: (info) => (
          <MDTypography
            variant="caption"
            sx={{
              fontWeight: 500,
              textAlign: 'right',
              display: 'block',
              paddingRight: 4,
            }}
          >
            {info.row.index + 1}
          </MDTypography>
        ),
        size: 24,
      },
      {
        accessorKey: 'status',
        header: '',
        cell: (info) => {
          const item = info.row.original;
          const hasCustomerProduct = !!item.selectedCustomerProductId;
          const hasProductVariant = !!item.selectedProductVariantId;
          const isMatched = hasCustomerProduct || hasProductVariant;

          const tooltipText = isMatched
            ? 'Item is matched. Click to change.'
            : 'Click to match this item to a product.';

          if (isAssociateProductV2Enabled && customerId) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SalesOrderDraftAssociateLineItemPopover
                      lineItem={item}
                      customerId={customerId}
                      onVariantSelected={({ productVariantId }) => {
                        onAssociateVariantSelected?.(
                          info.row.index,
                          item,
                          productVariantId,
                        );
                      }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <span className="text-xs">{tooltipText}</span>
                </TooltipContent>
              </Tooltip>
            );
          }

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex items-center justify-center w-full h-full cursor-pointer rounded p-1 hover:bg-muted"
                  onClick={() => {
                    if (onSelectLineItem) {
                      onSelectLineItem(info.row.index);
                    }
                  }}
                >
                  {isMatched ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <WarningIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <span className="text-xs">{tooltipText}</span>
              </TooltipContent>
            </Tooltip>
          );
        },
        size: 40,
      },
      {
        accessorKey: 'customerIdentifier',
        header: () => (
          <MDBox display="flex" alignItems="center" gap={0.8}>
            ID
            <Tooltip>
              <TooltipTrigger asChild>
                <MDBox sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <InfoIcon style={{ opacity: 0.7 }} size={16} />
                </MDBox>
              </TooltipTrigger>
              <TooltipContent>
                Customer&apos;s product identifier (e.g. SKU, UPC, GTIN, etc.)
              </TooltipContent>
            </Tooltip>
          </MDBox>
        ),
        cell: (info) => {
          const item = info.row.original;
          const identifier = item.customerIdentifier || item.sku || item.upc;

          return (
            <MDBox display="flex" flexDirection="column">
              {editable && onUpdateLineItem ? (
                <EditableCell
                  value={item.customerIdentifier || ''}
                  onSave={(newValue) =>
                    onUpdateLineItem(
                      info.row.index,
                      'customerIdentifier',
                      newValue,
                    )
                  }
                  inputType="text"
                  isPending={pendingUpdates[info.row.index]?.has(
                    'customerIdentifier',
                  )}
                  rowIndex={info.row.index}
                  field="customerIdentifier"
                  navigationManager={navigationManager}
                />
              ) : identifier ? (
                <MDTypography
                  variant="caption"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    display: 'block',
                  }}
                  title={identifier}
                >
                  {identifier}
                </MDTypography>
              ) : (
                <MDTypography variant="caption" color="text.secondary">
                  -
                </MDTypography>
              )}
            </MDBox>
          );
        },
        size: 100,
      },
      {
        accessorKey: 'customerDescription',
        header: () => (
          <MDBox display="flex" alignItems="center" gap={0.8}>
            Item
            <Tooltip>
              <TooltipTrigger>
                <InfoIcon className="opacity-70" size={16} />
              </TooltipTrigger>
              <TooltipContent>
                Customer&apos;s product description
              </TooltipContent>
            </Tooltip>
          </MDBox>
        ),
        cell: (info) => {
          const item = info.row.original;

          const description =
            item.customerDescription ||
            item.itemName ||
            item.name ||
            'No description available';

          return (
            <MDBox display="flex" flexDirection="column">
              {editable && onUpdateLineItem ? (
                <EditableCell
                  value={item.customerDescription || ''}
                  onSave={(newValue) =>
                    onUpdateLineItem(
                      info.row.index,
                      'customerDescription',
                      newValue,
                    )
                  }
                  inputType="text"
                  isPending={pendingUpdates[info.row.index]?.has(
                    'customerDescription',
                  )}
                  rowIndex={info.row.index}
                  field="customerDescription"
                  navigationManager={navigationManager}
                />
              ) : (
                <MDTypography variant="caption" color="text.secondary">
                  {description || 'No description available'}
                </MDTypography>
              )}
            </MDBox>
          );
        },
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        cell: (info) => {
          const value = info.getValue() as number;

          if (editable && onUpdateLineItem) {
            return (
              <EditableCell
                value={value}
                onSave={(newValue) =>
                  onUpdateLineItem(info.row.index, 'quantity', newValue)
                }
                type="integer"
                isPending={pendingUpdates[info.row.index]?.has('quantity')}
                rowIndex={info.row.index}
                field="quantity"
                navigationManager={navigationManager}
              />
            );
          }

          // Format quantity display: show whole numbers unless there's a decimal
          const displayValue = value
            ? value % 1 === 0
              ? value.toString()
              : value.toFixed(2)
            : '-';

          return <MDTypography variant="caption">{displayValue}</MDTypography>;
        },
        size: 80,
      },
      // List Price column - Oracle list price (read-only)
      {
        accessorKey: 'listPrice',
        header: 'List Price',
        cell: (info) => {
          const item = info.row.original;
          const discountValidation = getDiscountValidation(item);
          const listPrice = getListPrice(discountValidation);
          const isLoading = isDiscountLoading && hasLookupKey(item);

          if (isLoading) {
            return (
              <div className="flex items-center gap-1">
                <CircleNotchIcon
                  size={14}
                  className="text-blue-500 animate-spin"
                />
              </div>
            );
          }

          return (
            <MDTypography variant="caption">
              {formatCurrency(listPrice)}
            </MDTypography>
          );
        },
        size: 80,
      },
      // Discount % column - shows PO and ERP discount percentages
      {
        accessorKey: 'discountPercent',
        header: 'Discount %',
        cell: (info) => {
          const item = info.row.original;
          const discountValidation = getDiscountValidation(item);
          const listPrice = getListPrice(discountValidation);
          const erpUnitPrice = getErpUnitPrice(discountValidation);
          const poUnitPrice = item.unitPrice ?? null;

          const poDiscountPercent = calculateDiscountPercent(
            poUnitPrice,
            listPrice,
          );
          const erpDiscountPercent = calculateDiscountPercent(
            erpUnitPrice,
            listPrice,
          );

          return (
            <DualPriceCell
              poValue={poDiscountPercent}
              erpValue={erpDiscountPercent}
              format="percent"
              isLoading={isDiscountLoading && hasLookupKey(item)}
            />
          );
        },
        size: 100,
      },
      // Unit Price column - shows PO and ERP unit prices
      {
        accessorKey: 'unitPrice',
        header: 'Unit Price',
        cell: (info) => {
          const item = info.row.original;
          const discountValidation = getDiscountValidation(item);
          const erpUnitPrice = getErpUnitPrice(discountValidation);
          const poUnitPrice = item.unitPrice ?? null;

          return (
            <DualPriceCell
              poValue={poUnitPrice}
              erpValue={erpUnitPrice}
              format="currency"
              isLoading={isDiscountLoading && hasLookupKey(item)}
            />
          );
        },
        size: 100,
      },
      // Total Price column - shows PO and ERP totals
      {
        accessorKey: 'totalPrice',
        header: 'Total Price',
        cell: (info) => {
          const item = info.row.original;
          const discountValidation = getDiscountValidation(item);
          const erpUnitPrice = getErpUnitPrice(discountValidation);
          const poUnitPrice = item.unitPrice ?? null;
          const quantity = item.quantity ?? null;

          const poTotal = calculateTotal(quantity, poUnitPrice);
          const erpTotal = calculateTotal(quantity, erpUnitPrice);

          return (
            <DualPriceCell
              poValue={poTotal}
              erpValue={erpTotal}
              format="currency"
              isLoading={isDiscountLoading && hasLookupKey(item)}
            />
          );
        },
        size: 110,
      },
      // Price Status column - shows discount validation status badge
      {
        accessorKey: 'priceStatus',
        header: 'Price Status',
        cell: (info) => {
          const item = info.row.original;
          const rowIndex = info.row.index;
          const discountValidation = getDiscountValidation(item);
          // Get SKU from line item - use same logic as the ID column for consistency
          // This ensures the discount is created for the product shown in the ID column
          const sku = item.customerIdentifier || item.sku || item.upc;
          // Get productVariantId - either from direct selection or from SKU-based lookup
          const productVariantId = getProductVariantIdForItem(item);
          return (
            <DiscountBadge
              discountValidation={discountValidation}
              customerId={customerId}
              customerName={customerName}
              productVariantId={productVariantId}
              sku={sku}
              isLoading={isDiscountLoading && hasLookupKey(item)}
              customerSpecificDiscountVerification={
                item.customerSpecificDiscountVerification
              }
              onWaive={() => handleWaiveDiscount(rowIndex)}
              isWaiving={waivingLineItemIndex === rowIndex}
            />
          );
        },
        size: 120,
      },
      // Inventory Status column - conditionally included based on showInventoryColumn
      ...(showInventoryColumn
        ? [
            {
              accessorKey: 'inventoryValidation',
              header: 'Inventory Status',
              cell: (info: CellContext<LineItem, unknown>) => {
                const item = info.row.original;
                // Get inventory validation from async results if available
                const inventoryValidation = getInventoryValidation(item);
                return (
                  <InventoryBadge
                    inventoryValidation={inventoryValidation}
                    isLoading={isInventoryLoading && hasLookupKey(item)}
                  />
                );
              },
              size: 130,
            },
          ]
        : []),
      ...(isEdiSource
        ? [
            {
              accessorKey: 'rescheduledDate',
              header: 'Rescheduled Date',
              cell: (info: CellContext<LineItem, unknown>) => {
                const value = info.getValue() as string;

                if (editable && onUpdateLineItem) {
                  return (
                    <EditableDateCell
                      value={value}
                      onSave={(newValue) =>
                        onUpdateLineItem(
                          info.row.index,
                          'rescheduledDate',
                          newValue,
                        )
                      }
                      isPending={pendingUpdates[info.row.index]?.has(
                        'rescheduledDate',
                      )}
                      rowIndex={info.row.index}
                      field="rescheduledDate"
                      navigationManager={navigationManager}
                    />
                  );
                }

                return (
                  <MDTypography variant="caption">
                    {value
                      ? new Date(value + 'T12:00:00Z').toLocaleDateString()
                      : '-'}
                  </MDTypography>
                );
              },
              size: 120,
            },
          ]
        : []),
      ...(editable
        ? [
            {
              accessorKey: 'actions',
              header: '',
              cell: (info: CellContext<LineItem, unknown>) => {
                const isRejected = info.row.original.ediRejected;

                return (
                  <MDBox display="flex" gap={0.5}>
                    {isEdiSource ? (
                      // For EDI sources: show reject/restore toggle
                      <IconButton
                        size="small"
                        color={isRejected ? 'success' : 'warning'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleRejectLineItem?.(info.row.index);
                        }}
                        disabled={isLoading}
                        className="reject-action"
                        sx={{
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: isRejected
                              ? 'rgba(46, 125, 50, 0.04)'
                              : 'rgba(255, 152, 0, 0.04)',
                          },
                        }}
                      >
                        {isRejected ? (
                          <ArrowUUpLeftIcon size={14} />
                        ) : (
                          <ProhibitIcon size={14} />
                        )}
                      </IconButton>
                    ) : (
                      // For non-EDI sources: show delete
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteLineItem(info.row.index);
                        }}
                        disabled={isLoading}
                        className="delete-action"
                        sx={{
                          p: 0.5,
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.04)',
                          },
                        }}
                      >
                        <TrashIcon size={14} />
                      </IconButton>
                    )}
                  </MDBox>
                );
              },
              size: 25,
            } as ColumnDef<LineItem>,
          ]
        : []),
    ],
    [
      isAssociateProductV2Enabled,
      getInventoryValidation,
      isInventoryLoading,
      getDiscountValidation,
      getProductVariantIdForItem,
      isDiscountLoading,
      hasLookupKey,
      customerId,
      customerName,
      onAssociateVariantSelected,
      editable,
      onUpdateLineItem,
      isEdiSource,
      isLoading,
      onDeleteLineItem,
      onToggleRejectLineItem,
      pendingUpdates,
      navigationManager,
      onSelectLineItem,
      handleWaiveDiscount,
      waivingLineItemIndex,
      showInventoryColumn,
    ],
  );

  // Table setup following CustomerTable pattern
  const table = useReactTable({
    data: lineItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <MDBox
      ref={navigationManager.tableContainerRef}
      p={2}
      border="1px solid"
      borderColor="grey.300"
      borderRadius="borderRadius"
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <MDBox
        mb={2}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0 }}
      >
        <MDTypography variant="h6" fontWeight="medium">
          Line Items
        </MDTypography>
        <MDBox display="flex" gap={1} alignItems="center">
          {stagedPurchaseOrderId && (
            <Link
              to="/sales-order-drafts/$draftId/extraction-viewer"
              params={{ draftId: stagedPurchaseOrderId }}
            >
              <MDButton variant="outlined" color="secondary" size="small">
                <FileMagnifyingGlassIcon size={16} style={{ marginRight: 4 }} />
                View Source Mapping
              </MDButton>
            </Link>
          )}
        </MDBox>
      </MDBox>

      <TableContainer sx={{ boxShadow: 'none', flex: 1, overflow: 'auto' }}>
        <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
            }}
          >
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <TableCell
                    key={header.id}
                    sx={{
                      position:
                        header.id === 'lineNumber' ? 'sticky' : 'relative',
                      left: header.id === 'lineNumber' ? 0 : undefined,
                      zIndex: header.id === 'lineNumber' ? 2 : undefined,
                      textAlign: header.id === 'lineNumber' ? 'right' : 'left',
                      backgroundColor: 'background.paper',
                      boxShadow: 'none',
                      whiteSpace: 'nowrap',
                      width: header.column.columnDef.size
                        ? `${header.column.columnDef.size}px`
                        : 'auto',
                      minWidth: header.column.columnDef.size
                        ? `${header.column.columnDef.size}px`
                        : 'auto',
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : header.column.columnDef.header instanceof Function
                        ? header.column.columnDef.header(header.getContext())
                        : header.column.columnDef.header}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {table.getRowModel().rows.map((row) => {
              const isRejected = row.original.ediRejected;

              return (
                <React.Fragment key={row.id}>
                  {/* Main line item row */}
                  <TableRow
                    sx={{
                      '&:hover .delete-action': {
                        opacity: 1,
                      },
                      '&:hover .reject-action': {
                        opacity: 1,
                      },
                      '& .delete-action': {
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      },
                      '& .reject-action': {
                        opacity: 0,
                        transition: 'opacity 0.2s ease',
                      },
                      ...(isRejected && {
                        opacity: 0.6,
                        textDecoration: 'line-through',
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      }),
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          color: '#7b809a',
                          fontSize: '0.875rem',
                          fontWeight: '400',
                          ...(cell.column.id === 'lineNumber' && {
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            backgroundColor: 'background.paper',
                          }),
                          ...(isRejected && {
                            textDecoration: 'line-through',
                            opacity: 0.7,
                          }),
                        }}
                      >
                        {cell.column.columnDef.cell instanceof Function
                          ? cell.column.columnDef.cell(cell.getContext())
                          : String(cell.getValue() ?? '')}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}
